// src/pages/PrivacyPolicy.js
import React from 'react';

export default function PrivacyPolicy({ go }) {
  return (
    <div className="page-container" style={{ maxWidth: '1180px', margin: '0 auto', padding: '0 28px' }}>
      <div style={{ padding: '64px 0 40px', borderBottom: '1px solid #E3DACB' }}>
        <span style={{ 
          fontSize: '12.5px', 
          fontWeight: '700', 
          letterSpacing: '0.14em', 
          textTransform: 'uppercase',
          color: '#C96F22',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ width: '18px', height: '2px', background: '#C96F22', display: 'inline-block' }}></span>
          Legal
        </span>
        <h1 style={{ 
          fontFamily: "'Fraunces', serif",
          fontSize: 'clamp(32px, 4vw, 46px)',
          color: '#102844',
          marginTop: '14px'
        }}>Privacy Policy</h1>
        <p style={{ marginTop: '14px', color: '#5B6469', fontSize: '14.5px' }}>
          Last updated: June 19, 2026 &nbsp;·&nbsp; Applies to the YojanaSaathi mobile app and website
        </p>
      </div>

      <div style={{ padding: '56px 0 96px' }}>
        <h2 style={{ 
          fontFamily: "'Fraunces', serif",
          fontSize: '21px', 
          color: '#102844',
          margin: '38px 0 14px'
        }}>Overview</h2>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          YojanaSaathi ("we", "us", "our") operates an app and website that help citizens and businesses identify and apply for government schemes they may be eligible for. This Privacy Policy explains what information we collect, why we collect it, and how it is used and protected.
        </p>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          By using the YojanaSaathi app or website, you agree to the collection and use of information as described in this policy.
        </p>

        <div style={{
          background: '#FFFFFF',
          border: '1px solid #E3DACB',
          borderLeft: '4px solid #1F6E43',
          borderRadius: '10px',
          padding: '18px 20px',
          margin: '20px 0'
        }}>
          <p style={{ margin: 0, fontSize: '14.5px', color: '#5B6469' }}>
            <strong>In short:</strong> we collect the details you provide to match you with eligible schemes, and use them only for that purpose and to process the applications you choose to submit.
          </p>
        </div>

        <h2 style={{ 
          fontFamily: "'Fraunces', serif",
          fontSize: '21px', 
          color: '#102844',
          margin: '38px 0 14px'
        }}>Information we collect</h2>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          We collect information you provide directly when you create a profile or apply for a scheme. Depending on whether you're using the citizen or business flow, this may include:
        </p>
        <ul style={{ margin: '0 0 14px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>
            <strong>Personal details:</strong> name, age, gender, mobile number, email address, and address
          </li>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>
            <strong>Eligibility details:</strong> income, occupation, category, family details, and other information relevant to scheme eligibility
          </li>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>
            <strong>Business details:</strong> business name, registration details, sector, turnover, and employee count, for users applying on behalf of a business
          </li>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>
            <strong>Documents:</strong> identity, income, or other supporting documents you upload when applying to a specific scheme
          </li>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>
            <strong>Usage data:</strong> app interactions, device type, and approximate location, used to improve scheme matching and app performance
          </li>
        </ul>

        <h2 style={{ 
          fontFamily: "'Fraunces', serif",
          fontSize: '21px', 
          color: '#102844',
          margin: '38px 0 14px'
        }}>How we use your information</h2>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          We use the information you provide to:
        </p>
        <ul style={{ margin: '0 0 14px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>Match your profile against eligibility criteria for government schemes</li>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>Pre-fill and submit applications you choose to apply for</li>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>Show you the status of applications you've submitted through the app</li>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>Improve the accuracy of our scheme-matching engine</li>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>Communicate with you about your applications or account</li>
        </ul>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          We do not use your personal or eligibility information for advertising, and we do not sell your data to third parties.
        </p>

        <h2 style={{ 
          fontFamily: "'Fraunces', serif",
          fontSize: '21px', 
          color: '#102844',
          margin: '38px 0 14px'
        }}>Sharing of information</h2>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          We share your information only in the following circumstances:
        </p>
        <ul style={{ margin: '0 0 14px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>
            <strong>With government departments or portals,</strong> when you choose to apply for a specific scheme, to the extent necessary to process that application
          </li>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>
            <strong>With service providers</strong> who help us operate the app (such as cloud hosting), under confidentiality obligations
          </li>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>
            <strong>When required by law,</strong> such as in response to a valid legal request from a government authority
          </li>
        </ul>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          We do not share your information with advertisers or data brokers.
        </p>

        <h2 style={{ 
          fontFamily: "'Fraunces', serif",
          fontSize: '21px', 
          color: '#102844',
          margin: '38px 0 14px'
        }}>Data storage & security</h2>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          Your information is stored on secure servers with encryption in transit and at rest. We use industry-standard safeguards, including access controls and regular security reviews, to protect your data from unauthorized access, alteration, or disclosure.
        </p>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          No method of transmission or storage is completely secure, and while we work to protect your information, we cannot guarantee absolute security.
        </p>

        <h2 style={{ 
          fontFamily: "'Fraunces', serif",
          fontSize: '21px', 
          color: '#102844',
          margin: '38px 0 14px'
        }}>Data retention</h2>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          We retain your profile and application information for as long as your account is active, or as needed to provide the service and comply with legal obligations. You may request deletion of your data at any time, as described below.
        </p>

        <h2 style={{ 
          fontFamily: "'Fraunces', serif",
          fontSize: '21px', 
          color: '#102844',
          margin: '38px 0 14px'
        }}>Your rights & choices</h2>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>You can:</p>
        <ul style={{ margin: '0 0 14px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>Access, review, or update the details in your profile at any time within the app</li>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>Request a copy of the personal information we hold about you</li>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>Request that we delete your account and associated data using our <a href="#" onClick={(e) => { e.preventDefault(); go('delete-request'); }} style={{ color: '#1B3A5C', fontWeight: 600 }}>Data Deletion Request page</a>. We will process your request within 7 business days, subject to records we're legally required to retain</li>
          <li style={{ fontSize: '15px', lineHeight: '1.65', color: '#23282B' }}>Withdraw consent for non-essential data uses, where applicable</li>
        </ul>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          To exercise any of these rights, contact us using the details below.
        </p>

        <h2 style={{ 
          fontFamily: "'Fraunces', serif",
          fontSize: '21px', 
          color: '#102844',
          margin: '38px 0 14px'
        }}>Children's privacy</h2>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          YojanaSaathi is not intended for use by children under 18. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us so we can remove it.
        </p>

        <h2 style={{ 
          fontFamily: "'Fraunces', serif",
          fontSize: '21px', 
          color: '#102844',
          margin: '38px 0 14px'
        }}>Changes to this policy</h2>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          We may update this Privacy Policy from time to time. Material changes will be reflected by an updated "Last updated" date at the top of this page, and where appropriate, communicated within the app.
        </p>

        <h2 style={{ 
          fontFamily: "'Fraunces', serif",
          fontSize: '21px', 
          color: '#102844',
          margin: '38px 0 14px'
        }}>Contact us</h2>
        <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#23282B', marginBottom: '14px' }}>
          If you have questions about this Privacy Policy or how your information is handled, reach us at <a href="mailto:yojanasaathi@gmail.com" style={{ color: '#1B3A5C' }}>yojanasaathi@gmail.com</a> or via our <a href="#" onClick={(e) => { e.preventDefault(); go('contact'); }} style={{ color: '#1B3A5C' }}>contact page</a>.
        </p>
      </div>
    </div>
  );
}